/**
 * PlayerAvatar — avatar circolare del giocatore.
 *
 * Andrea 2026-05-21: ha messo nella cartella ~/Desktop/fantasquad/*.png
 * delle foto giocatori (resize automatico via scripts/optimize_avatars.py
 * → frontend/public/avatars/<nome-normalizzato>.png).
 *
 * Strategia matching nome → file:
 * 1. Normalizza il nome del giocatore con la stessa logica dello script
 *    Python (lowercase, NFKD, hyphen, no accents).
 * 2. Prova varie permutazioni (cognome, nome+cognome, ecc.) cercando
 *    nel manifest `AVATAR_NAMES` (lista delle foto effettivamente
 *    presenti).
 * 3. Se trovato → <img src="/avatars/<match>.png" />
 * 4. Altrimenti → fallback iniziali su gradient viola.
 *
 * Il fallback evita 404 e non spreca richieste HTTP — sappiamo a priori
 * se la foto esiste.
 */

import { AVATAR_NAMES, HAS_GENERIC_AVATAR } from "../../data/avatars-manifest";
import { type IconaInfo, urlIcona, useIconeGiocatori } from "../../hooks/useIconeGiocatori";
import { tr } from "../../i18n";

interface PlayerAvatarProps {
  nome: string | null | undefined;
  /** Dimensione lato in px (default 40). */
  size?: number;
  /** Classe extra per il container (es. opacity/ring per stati slot). */
  className?: string;
}

/** Normalizza un nome libero in slug URL-safe.
 * `splitCamelCase=true` genera "thuram-m" da "thuramM" (utile per
 * suffissi tipo iniziali), `false` produce "mctominay" da "McTominay"
 * (utile quando il file e' senza separatori). Generiamo entrambe le
 * varianti nel matching per coprire entrambi i casi. */
function normalizeForFile(name: string, splitCamelCase = true): string {
  let s = name;
  // Pre-replace caratteri non decomponibili NFKD
  s = s
    .replace(/[øØ]/g, (c) => (c === "ø" ? "o" : "O"))
    .replace(/[łŁ]/g, (c) => (c === "ł" ? "l" : "L"))
    .replace(/[æ]/g, "ae")
    .replace(/[Æ]/g, "AE")
    .replace(/[ß]/g, "ss")
    .replace(/[ðÐ]/g, (c) => (c === "ð" ? "d" : "D"));
  // NFKD per separare accenti, poi rimuovi i combining
  s = s.normalize("NFKD").replace(/[̀-ͯ]/g, "");
  if (splitCamelCase) {
    s = s.replace(/([a-z0-9])([A-Z])/g, "$1-$2");
  }
  s = s.toLowerCase();
  s = s.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return s;
}

/** Parole che NON identificano un giocatore: da sole non devono mai far
 * scattare un match (Andrea 2026-09-19). Senza questo "Iling Junior" (Pisa)
 * pescava l'icona di "Robinho Junior" (Genoa) per il suffisso "junior". */
const TOKEN_GENERICI = new Set(["junior", "jr", "senior", "sr"]);

/** Cerca nel manifest un nome avatar che combaci col giocatore.
 * Andrea 2026-05-21: matching tollerante per gestire varie convenzioni
 * di naming dei file Andrea (con/senza hyphen, con/senza nome,
 * apostrofi, ecc.). Strategie a cascata. */
function findAvatarFile(nome: string): string | null {
  // Genera set di candidati normalizzati (rimuovi duplicati)
  const variants = new Set<string>();
  const addVariant = (s: string | null | undefined) => {
    if (!s) return;
    const a = normalizeForFile(s, true);
    const b = normalizeForFile(s, false);
    if (a) variants.add(a);
    if (b) variants.add(b);
    // Andrea 2026-05-22: variante "concat" senza hyphen, per file tipo
    // "pioesposito.png", "dacunha.png", "ndicka.png".
    if (a) variants.add(a.replace(/-/g, ""));
    if (b) variants.add(b.replace(/-/g, ""));
  };

  // Andrea 2026-09-01: se il nome listone porta il suffisso-iniziali
  // ("Martinez L.", "Konè I.") NON generare varianti col solo cognome:
  // gli omonimi senza icona propria matchavano l'icona dell'altro
  // (Josep Martinez prendeva la faccia di Lautaro). Convenzione file
  // per questi giocatori: "cognome-iniziale" (es. martinez-l.png).
  // Ultima parola = iniziale/i puntate: "L.", "Jo.", "Gia.", "F.P."
  const hasIniziali = /\s+[A-ZÀ-Ý][A-Za-zÀ-ý.]{0,4}\.$/.test(nome.trim());

  // 1. Nome completo
  addVariant(nome);

  if (!hasIniziali) {
    // 2. Tokens individuali (cognome, nome, ecc.), saltando i generici
    const tokens = nome.trim().split(/\s+/);
    for (const t of tokens) {
      if (TOKEN_GENERICI.has(normalizeForFile(t, false))) continue;
      addVariant(t);
    }

    // 3. Primo + ultimo (per nomi composti)
    if (tokens.length >= 2) {
      addVariant(tr("{{v1}} {{v2}}", { v1: tokens[0], v2: tokens[tokens.length - 1] }));
    }
  }

  // Step A: match diretto su una variante
  for (const v of variants) {
    if (v && AVATAR_NAMES.has(v)) return v;
  }

  // Step B: match reverso — un token del nome combacia con un token del
  // filename multi-parte. Es: DB "Martinez" → "lautaro-martinez".
  for (const file of AVATAR_NAMES) {
    const fileTokens = file.split("-");
    for (const v of variants) {
      if (v && v.length >= 3 && fileTokens.includes(v)) return file;
    }
  }

  // Step C: match sul SUFFISSO — per filename SINGOLO token (no hyphen)
  // che concatena nome + cognome: il cognome del DB è la parte finale,
  // e deve avere almeno 5 char (evita falsi positivi tipo "ber" in
  // "barella"). Es: DB "Pellegrini" → "lorenzopellegrini".
  // Andrea 2026-05-22; ristretto al suffisso il 2026-09-08: con
  // `includes` il file "carlosaugusto" catturava anche Diego Carlos
  // (Parma) e Kevin Carlos (Cagliari), che si ritrovavano in campo la
  // faccia di Carlos Augusto.
  // Andrea 2026-09-24: e quel che precede il cognome nel file deve essere
  // un'altra parola del nome del giocatore ("diegocarlos" e' Diego Carlos,
  // non Kevin Carlos): prima bastava il cognome in coda.
  const parole = new Set(
    nome
      .trim()
      .split(/\s+/)
      .map((t) => normalizeForFile(t, false))
      .filter(Boolean),
  );
  for (const file of AVATAR_NAMES) {
    if (file.includes("-")) continue; // gestito allo step B
    for (const v of variants) {
      if (!v || v.length < 5) continue;
      if (file.endsWith(v) && file !== v && parole.has(file.slice(0, -v.length))) return file;
    }
  }

  return null;
}

function computeInitials(nome: string): string {
  const parts = nome
    .trim()
    .replace(/\s+[A-ZÀ-Ý](?:\.[A-ZÀ-Ý])*\.?\s*$/, "")
    .trim()
    .split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** C'e' un'icona statica in /public/avatars per questo nome? */
export function haIconaStatica(nome: string | null | undefined): boolean {
  return !!nome && findAvatarFile(nome) !== null;
}

/** URL dell'icona del giocatore con la stessa cascata del componente
 * (icona fatta in app → file in /avatars → generico), per chi deve
 * disegnarla fuori dal DOM (immagini da condividere, 2026-10-08). */
export function urlIconaGiocatore(
  nome: string | null | undefined,
  iconeApp: Map<string, IconaInfo> | undefined,
): string | null {
  if (!nome) return null;
  const iconaApp = iconeApp?.get(nome);
  if (iconaApp) return urlIcona(iconaApp);
  const file = findAvatarFile(nome);
  if (file) return `/avatars/${file}.png`;
  return HAS_GENERIC_AVATAR ? "/avatars/generico.png" : null;
}

export function PlayerAvatar({ nome, size = 40, className = "" }: PlayerAvatarProps) {
  const px = `${size}px`;
  // Andrea 2026-10-01: prima le icone fatte col generatore in app (salvate
  // sul server, per giocatore), poi quelle statiche, poi la generica.
  const { data: iconeApp } = useIconeGiocatori();
  const iconaApp = nome ? iconeApp?.get(nome) : undefined;
  if (iconaApp) {
    return (
      <div
        className={`rounded-full overflow-hidden bg-bg-card border border-primary/30 ${className}`}
        style={{ width: px, height: px }}
      >
        <img
          src={urlIcona(iconaApp)}
          alt={nome ?? ""}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }
  if (!nome) {
    return (
      <div
        className={`rounded-full bg-fg-muted/30 ${className}`}
        style={{ width: px, height: px }}
      />
    );
  }
  const avatarFile = findAvatarFile(nome);
  if (avatarFile) {
    return (
      <div
        className={`rounded-full overflow-hidden bg-bg-card border border-primary/30 ${className}`}
        style={{ width: px, height: px }}
      >
        <img
          src={`/avatars/${avatarFile}.png`}
          alt={nome}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }
  // Andrea 2026-05-21: fallback "generico" se presente in /public/avatars/.
  // Mostra una sagoma silhouette comune invece delle iniziali quando manca
  // la foto specifica del giocatore.
  if (HAS_GENERIC_AVATAR) {
    return (
      <div
        className={`rounded-full overflow-hidden bg-bg-card border border-primary/30 ${className}`}
        style={{ width: px, height: px }}
        title={nome}
      >
        <img
          src="/avatars/generico.png"
          alt={nome}
          className="w-full h-full object-cover opacity-90"
          loading="lazy"
        />
      </div>
    );
  }
  // Ultimo fallback: iniziali su gradient
  const initials = computeInitials(nome);
  return (
    <div
      className={`rounded-full bg-gradient-to-br from-primary to-primary-deep text-accent font-display flex items-center justify-center ${className}`}
      style={{
        width: px,
        height: px,
        fontSize: `${Math.round(size * 0.42)}px`,
      }}
    >
      {initials}
    </div>
  );
}
